import { Drawer, DrawerContent, DrawerOverlay } from '@/components/ui/drawer'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { normalizeUrl, simplifyUrl } from '@/lib/url'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { HugeiconsIcon } from '@hugeicons/react'
import { Add01Icon, CheckIcon } from '@hugeicons/core-free-icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { recentRelayFeedsAtom } from '@/atoms/feed'
import { trackRecentRelayFeed } from '@/PageManager'
import { Input } from '@/components/ui/input'
import RelayIcon from './RelayIcon'

export type Item =
  | { kind: 'relay'; url: string }
  | { kind: 'relay-set'; identifier: string; relays: string[]; name: string }
  | { kind: 'item'; id: string; label: string }
  | { kind: 'separator' }

export type SelectableItem = Exclude<Item, { kind: 'separator' }>

function idsToSelectableItems(items: Item[], ids: Set<string>) {
  return Array.from(ids)
    .map((id) => {
      const found = items.find((item) =>
        item.kind === 'relay'
          ? item.url === id
          : item.kind === 'relay-set'
            ? item.identifier === id
            : item.kind === 'item'
              ? item.id === id
              : false
      )
      if (found) return found as SelectableItem
      return { kind: 'relay' as const, url: id }
    })
    .filter((item): item is SelectableItem => !!item)
}

export default function RelaySelector({
  items,
  selected,
  onChange,
  onClose,
  children,
  hideChangedDot = false
}: {
  items: Item[]
  selected: Set<string>
  onChange: (sel: SelectableItem[]) => void
  onClose?: () => void
  children: React.ReactNode
  hideChangedDot?: boolean
}) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const { t } = useTranslation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [sel, setSelected] = useState<Set<string>>(selected)
  const [customUrls, setCustomUrls] = useState<string[]>([])
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customInputValue, setCustomInputValue] = useState('')
  const customInputRef = useRef<HTMLInputElement>(null)
  const recentFeeds = useAtomValue(recentRelayFeedsAtom)

  useEffect(() => {
    setSelected(selected)
  }, [selected])

  useEffect(() => {
    if (showCustomInput) customInputRef.current?.focus()
  }, [showCustomInput])

  const selectedRelayUrls = useMemo(() => {
    const urls: string[] = []
    for (const id of sel) {
      const item = items.find((i) =>
        i.kind === 'relay'
          ? i.url === id
          : i.kind === 'relay-set'
            ? i.identifier === id
            : i.kind === 'item'
              ? i.id === id
              : false
      )
      if (item?.kind === 'relay') urls.push(item.url)
      else if (item?.kind === 'relay-set') urls.push(...item.relays)
    }
    return urls
  }, [items, sel])
  const selectedRelayTitle = selectedRelayUrls.length > 0 ? selectedRelayUrls.join('\n') : undefined

  const existingRelayUrls = useMemo(
    () =>
      new Set(
        items
          .filter((i): i is Item & { kind: 'relay' } => i.kind === 'relay')
          .map((i) => normalizeUrl(i.url))
      ),
    [items]
  )

  const hardcodedRelayUrls = useMemo(() => {
    try {
      const feeds = window.hallway?.universe?.hardcodedFeeds
      if (!feeds) return []
      const urls = new Set<string>()
      for (const feed of feeds) {
        for (const relay of feed.relays || []) {
          const normalized = normalizeUrl(relay)
          if (!existingRelayUrls.has(normalized)) urls.add(normalized)
        }
      }
      return Array.from(urls)
    } catch {
      return []
    }
  }, [existingRelayUrls])

  const initial = useRef(Array.from(selected.values()))
  const isChanged = useMemo(() => {
    if (hideChangedDot) return false
    return initial.current.length !== sel.size || !initial.current.every((v) => sel.has(v))
  }, [hideChangedDot, sel])

  const recentRelayItems = recentFeeds
    .filter((ref): ref is string => typeof ref === 'string')
    .filter((ref) => !existingRelayUrls.has(normalizeUrl(ref)))
    .filter((ref) => !customUrls.includes(normalizeUrl(ref)))

  const content = (
    <>
      {recentRelayItems.map((url) => (
        <MenuItem
          key={url}
          id={normalizeUrl(url)}
          checked={sel.has(normalizeUrl(url))}
          onCheckedChange={() => selectUrl(url)}
        >
          <div className="flex items-center gap-2">
            <RelayIcon url={url} />
            <div className="flex-1 truncate text-left">{simplifyUrl(url)}</div>
          </div>
        </MenuItem>
      ))}
      {items.map((item, i) => {
        if (item.kind === 'separator') {
          return <MenuSeparator key={`sep-${i}`} />
        }
        if (item.kind === 'relay') {
          return (
            <MenuItem
              key={item.url}
              id={item.url}
              checked={sel.has(item.url)}
              onCheckedChange={handleChange}
            >
              <div className="flex items-center gap-2">
                <RelayIcon url={item.url} />
                <div className="truncate">{simplifyUrl(item.url)}</div>
              </div>
            </MenuItem>
          )
        }
        if (item.kind === 'relay-set') {
          return (
            <MenuItem
              id={item.identifier}
              key={item.identifier}
              checked={sel.has(item.identifier)}
              onCheckedChange={handleChange}
            >
              <div className="truncate">
                {item.name} ({item.relays.length})
              </div>
            </MenuItem>
          )
        }
        return (
          <MenuItem
            key={item.id}
            id={item.id}
            checked={sel.has(item.id)}
            onCheckedChange={handleChange}
          >
            <div className="truncate">{item.label}</div>
          </MenuItem>
        )
      })}
      {hardcodedRelayUrls.length > 0 && items.length > 0 && <MenuSeparator key="hardcoded-sep" />}
      {hardcodedRelayUrls.map((url) => (
        <MenuItem key={url} id={url} checked={sel.has(url)} onCheckedChange={handleChange}>
          <div className="flex items-center gap-2">
            <RelayIcon url={url} />
            <div className="truncate">{simplifyUrl(url)}</div>
          </div>
        </MenuItem>
      ))}
      {(items.length > 0 || hardcodedRelayUrls.length > 0) && <MenuSeparator key="custom-sep" />}
      {customUrls.map((url) => (
        <MenuItem
          key={url}
          id={url}
          checked={sel.has(url)}
          onCheckedChange={() => {
            setCustomUrls((prev) => prev.filter((u) => u !== url))
            setSelected((prev) => {
              const next = new Set(prev)
              next.delete(url)
              onChange(idsToSelectableItems(items, next))
              return next
            })
          }}
        >
          <div className="flex items-center gap-2">
            <RelayIcon url={url} />
            <div className="flex-1 truncate text-left">{simplifyUrl(url)}</div>
          </div>
        </MenuItem>
      ))}
      {customUrls.length > 0 && <MenuSeparator />}
      {showCustomInput ? (
        <div className="flex items-center gap-1 px-2 py-1.5">
          <Input
            ref={customInputRef}
            value={customInputValue}
            onChange={(e) => setCustomInputValue(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') {
                const raw = customInputValue.trim()
                if (!raw) return
                const url = /^wss?:\/\//i.test(raw) ? raw : 'wss://' + raw
                selectUrl(url)
                setCustomInputValue('')
                setShowCustomInput(false)
              }
              if (e.key === 'Escape') {
                setShowCustomInput(false)
                setCustomInputValue('')
              }
            }}
            onKeyUp={(e) => e.stopPropagation()}
            placeholder={t('relay.example.com')}
            className="h-8 text-sm"
          />
        </div>
      ) : (
        <CustomRelayItem onClick={() => setShowCustomInput(true)} />
      )}
    </>
  )

  if (isSmallScreen) {
    return (
      <>
        <div onClick={() => setIsDrawerOpen(true)}>
          <div className="relative inline-flex" title={selectedRelayTitle}>
            {children}
            {isChanged && (
              <div className="absolute top-1.75 left-7 size-2 rounded-full bg-primary ring-2 ring-background pointer-events-none" />
            )}
          </div>
        </div>
        <Drawer
          open={isDrawerOpen}
          onOpenChange={(open) => {
            setIsDrawerOpen(open)
            if (!open) onClose?.()
          }}
        >
          <DrawerOverlay onClick={() => setIsDrawerOpen(false)} />
          <DrawerContent className="max-h-[80vh]" hideOverlay>
            <div className="overflow-y-auto overscroll-contain py-2 touch-pan-y">{content}</div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) onClose?.()
      }}
    >
      <DropdownMenuTrigger asChild>
        <div className="relative inline-flex">
          {children}
          {isChanged && (
            <div className="absolute top-1.75 left-7 size-2 rounded-full bg-primary ring-2 ring-background pointer-events-none" />
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-w-96 max-h-[50vh]"
        showScrollButtons
        onOpenAutoFocus={(e: Event) => e.preventDefault()}
      >
        {content}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  function handleChange(id: string, checked: boolean) {
    setSelected((prev) => {
      if (checked) prev.add(id)
      else prev.delete(id)
      onChange(idsToSelectableItems(items, prev))
      return new Set(prev)
    })
  }

  function selectUrl(url: string) {
    trackRecentRelayFeed(url)
    const normalized = normalizeUrl(url)
    setCustomUrls((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]))
    setSelected((prev) => {
      if (prev.has(normalized)) return prev
      const next = new Set(prev)
      next.add(normalized)
      onChange(idsToSelectableItems(items, next))
      return next
    })
  }
}

function CustomRelayItem({ onClick }: { onClick: () => void }) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const { t } = useTranslation()

  if (isSmallScreen) {
    return (
      <div
        onClick={onClick}
        className="flex items-center gap-2 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30"
      >
        <div className="flex items-center justify-center size-4 shrink-0">
          <HugeiconsIcon icon={Add01Icon} className="size-4" />
        </div>
        <span className="text-muted-foreground">{t('Custom relay URL')}</span>
      </div>
    )
  }

  return (
    <DropdownMenuCheckboxItem
      onSelect={(e) => {
        e.preventDefault()
        onClick()
      }}
      checked={false}
      className="flex items-center gap-2 text-muted-foreground"
    >
      <HugeiconsIcon icon={Add01Icon} className="size-4" />
      <span>{t('Custom relay URL')}</span>
    </DropdownMenuCheckboxItem>
  )
}

function MenuSeparator() {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  if (isSmallScreen) {
    return <Separator />
  }
  return <DropdownMenuSeparator />
}

function MenuItem({
  id,
  children,
  checked,
  onCheckedChange
}: {
  id: string
  children: React.ReactNode
  checked: boolean
  onCheckedChange: (id: string, checked: boolean) => void
}) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)

  if (isSmallScreen) {
    return (
      <div
        onClick={() => onCheckedChange(id, !checked)}
        className="flex items-center gap-2 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30"
      >
        <div className="flex items-center justify-center size-4 shrink-0">
          {checked && <HugeiconsIcon icon={CheckIcon} className="size-4" />}
        </div>
        {children}
      </div>
    )
  }

  return (
    <DropdownMenuCheckboxItem
      checked={checked}
      onSelect={(e) => e.preventDefault()}
      onCheckedChange={(checked) => onCheckedChange(id, checked)}
      className="flex items-center gap-2"
    >
      {children}
    </DropdownMenuCheckboxItem>
  )
}
